import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import api from '../../lib/api';
import { Users, CreditCard, FileText, TrendingUp, ShieldAlert } from 'lucide-react';

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needs2FA, setNeeds2FA] = useState(false);

  useEffect(() => {
    fetchStats();
    check2FA();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const check2FA = async () => {
    try {
      const res = await api.get('/auth/2fa/status');
      if (!res.data.enabled) setNeeds2FA(true);
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: <Users className="w-6 h-6" />, color: 'bg-[#E8F0FE] text-[#1A73E8]' },
    { label: 'Paid Users', value: stats?.paid_users || 0, icon: <TrendingUp className="w-6 h-6" />, color: 'bg-[#E6F4EA] text-[#1E8E3E]' },
    { label: 'Total Posts', value: stats?.total_posts || 0, icon: <FileText className="w-6 h-6" />, color: 'bg-[#FEF7E0] text-[#F9AB00]' },
    { label: 'Total Revenue', value: `$${stats?.total_revenue?.toFixed(2) || '0.00'}`, icon: <CreditCard className="w-6 h-6" />, color: 'bg-[#F3E8FD] text-[#9334E9]' },
  ];

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* 2FA Setup Advisory */}
      {needs2FA && (
        <div className="bg-[#FEF7E0] border border-[#F9AB00] rounded-xl p-4 flex items-center justify-between" data-testid="2fa-advisory-banner">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-6 h-6 text-[#E37400]" />
            <div>
              <p className="font-medium text-[#202124] text-sm">Two-Factor Authentication Required</p>
              <p className="text-xs text-[#5F6368]">Protect your admin account by enabling 2FA with Google Authenticator.</p>
            </div>
          </div>
          <Link to="/gmbadmin/profile" className="px-4 py-2 bg-[#F9AB00] text-white rounded-lg text-sm font-medium hover:bg-[#E37400] whitespace-nowrap" data-testid="setup-2fa-link">
            Set Up Now
          </Link>
        </div>
      )}

      <div>
        <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
          Admin Dashboard
        </h1>
        <p className="text-[#5F6368]">Overview of your GMB Autopilot platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <div key={i} className="dashboard-card">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                {stat.icon}
              </div>
              <div>
                <p className="text-sm text-[#5F6368]">{stat.label}</p>
                <p className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
                  {stat.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="dashboard-card">
          <h3 className="text-lg font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
            User Distribution
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[#5F6368]">Free Users</span>
              <span className="font-semibold text-[#202124]">{stats?.free_users || 0}</span>
            </div>
            <div className="w-full h-2 bg-[#DADCE0] rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#1A73E8] rounded-full"
                style={{ width: `${((stats?.free_users || 0) / (stats?.total_users || 1)) * 100}%` }}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#5F6368]">Paid Users</span>
              <span className="font-semibold text-[#202124]">{stats?.paid_users || 0}</span>
            </div>
            <div className="w-full h-2 bg-[#DADCE0] rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#1E8E3E] rounded-full"
                style={{ width: `${((stats?.paid_users || 0) / (stats?.total_users || 1)) * 100}%` }}
              />
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <h3 className="text-lg font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
            Quick Actions
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <a href="/gmbadmin/users" className="p-4 bg-[#F8F9FA] rounded-lg hover:bg-[#F1F3F4] transition-colors text-center">
              <Users className="w-6 h-6 mx-auto text-[#5F6368]" />
              <p className="text-sm font-medium mt-2">Manage Users</p>
            </a>
            <a href="/gmbadmin/membership" className="p-4 bg-[#F8F9FA] rounded-lg hover:bg-[#F1F3F4] transition-colors text-center">
              <CreditCard className="w-6 h-6 mx-auto text-[#5F6368]" />
              <p className="text-sm font-medium mt-2">Edit Plans</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
